import { FC, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AccordionHeader,
  AccordionHeaderText,
  ChipOptionsDataType,
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiModal,
} from "../../components";
import { MuiDoubleLabelWithChip } from "../../components/doubleLabelWithChip/MuiDoubleLabelWithChip";
import { useSiteCore } from "../../hooks/useSiteCore";
import { toggleLoader } from "../../redux/slices/appState/appStateSlice";
import { useGridContainer } from "../../hooks/useGridContainer";
import { useMdmGetViewActivityDetailById } from "../../api/services/mdmActivity/mdmActivityApis";
import {
  mdmAddActivitySiteCoreKeys,
  mdmAddActivityTopicSiteCoreKeys,
} from "../mdmAddActivity/siteCore/SiteCoreKeys";
import {
  getValueOrBlank,
  isNotEmpty,
  replaceHashWithValue,
} from "../../helpers/utils";
import useAccordion from "../../hooks/useAccordion";
import { ActivityTopicList } from "../mdmAddActivity/components/ActivityTopicList/ActivityTopicList";
import {
  MDM_ACTIVITY_SEARCH_TYPE_KEY,
  PAGE_IDS,
} from "../../helpers/constants";
import { MdmViewActivityWrapper } from "./MdmViewActivity.style";
import { IdNameList } from "../../api/services/common/models";
import {
  setActivityFramework,
  setActivityTopicList,
  updateSelectedUnit,
} from "../../redux/slices/mdmActivity/mdmActivitySlice";
import { UnitTypesKey } from "../../helpers/constants/siteCoreKeys";
import {
  ActivityExpectations,
  ActivityLocale,
  TopicListResponseItem,
} from "../../redux/slices/mdmActivity/models";
import { useToast } from "../../hooks/useToast";
import { topicListSelector } from "../../redux/slices/mdmActivity/mdmActivitySelector";
import { useNavigationSiteCore } from "../../hooks/useNavigationSiteCore";

const pageId = PAGE_IDS.MDM_VIEW_ACTIVITY;

const initialAccordionStatus = {
  activityDetails: true,
  topics: true,
  all: true,
};

interface MdmViewActivityProps {
  activityId: string;
  show: boolean;
  handleCloseModal: () => void;
  editButtonClick?: () => void;
  handleViewTopic?: (topicId: string) => void;
}

export const MdmViewActivity: FC<MdmViewActivityProps> = ({
  show,
  handleCloseModal,
  activityId,
  editButtonClick,
  handleViewTopic,
}) => {
  const frameworkRef = useRef<HTMLDivElement>(null);
  const modalityRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);
  const { accordionStatus, handleIndividualAccordionChange } = useAccordion(
    initialAccordionStatus,
  );
  const topicList = useSelector(topicListSelector);

  const {
    commonLabels,
    fetchLabel,
    fetchValueByKeyOfDropdown,
    fetchComponentTitle,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const {
    data: activityData,
    isFetching,
    isError: isActivityDataError,
    refetchWithParams: refetchActivityData,
  } = useMdmGetViewActivityDetailById();

  useEffect(() => {
    if (activityId) {
      refetchActivityData({ id: activityId });
    }
  }, [activityId]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isFetching,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isFetching]);

  useEffect(() => {
    if (isActivityDataError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isActivityDataError]);

  const isSearchTypeProgram = useMemo(
    () => activityData?.type === MDM_ACTIVITY_SEARCH_TYPE_KEY.program,
    [activityData?.type],
  );

  const getViewActivityLabel = (key: string) => {
    return fetchLabel(mdmAddActivitySiteCoreKeys.activityDetailsKey, key);
  };

  const renderHeader = (labelKey: string) => {
    return <AccordionHeaderText label={fetchComponentTitle(labelKey)} />;
  };

  const getTopicList = () => {
    if (isNotEmpty(activityData)) {
      return activityData?.topics.map((element: TopicListResponseItem) => ({
        isExisting: true,
        unit: element.units,
        candidateStatus: element.participantStatus,
        localizedTopics: element.locale.map((locale: ActivityLocale) => ({
          name: locale.localeName,
          ...locale,
        })),
        defaultValueExceptions: element.expectations.map(
          (expectation: ActivityExpectations) => ({
            country: expectation?.countryTitle,
            units: expectation?.unit,
            measurementName:
              expectation.unit === UnitTypesKey.Minutes
                ? Number(
                    fetchValueByKeyOfDropdown(
                      mdmAddActivityTopicSiteCoreKeys.topicListKey,
                      mdmAddActivityTopicSiteCoreKeys.minutesList,
                      expectation.measurement,
                    ),
                  )
                : Number(expectation.measurement),
            ...expectation,
          }),
        ),
        ...element,
      }));
    }
    return [];
  };

  useEffect(() => {
    if (isNotEmpty(activityData)) {
      dispatch(updateSelectedUnit(activityData?.unit ?? ""));
      dispatch(setActivityTopicList(getTopicList()));
      dispatch(setActivityFramework(activityData?.type));
    }
  }, [activityData]);

  const frameworkLabel = useMemo(
    () =>
      getViewActivityLabel(
        isSearchTypeProgram
          ? mdmAddActivitySiteCoreKeys.programFramework
          : mdmAddActivitySiteCoreKeys.projectFramework,
      ),
    [isSearchTypeProgram],
  );

  return (
    <MuiModal
      open={show}
      onClose={handleCloseModal}
      title={replaceHashWithValue(
        getViewActivityLabel(mdmAddActivitySiteCoreKeys.viewActivityHeader),
        getValueOrBlank(activityData?.activityName),
      )}
      secondaryButtonTitle={commonLabels.close}
      onSecondaryButtonClick={handleCloseModal}
      tertiaryButtonTitle={getViewActivityLabel(
        mdmAddActivitySiteCoreKeys.editActivityLink,
      )}
      onTertiaryButtonClick={editButtonClick}
      hasLargeContent
      className="mdm-view-activity-modal"
    >
      <MdmViewActivityWrapper>
        <AccordionHeader
          className="accordion-header"
          title={renderHeader(mdmAddActivitySiteCoreKeys.activityDetailsKey)}
          onChange={() => handleIndividualAccordionChange("activityDetails")}
          isExpanded={accordionStatus.activityDetails}
        >
          <MuiGrid
            container
            data-testid="activity-details"
            className="activity-details-grid"
          >
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDoubleLabelWithChip
                label={frameworkLabel}
                replaceEmptyWithDash
                value={
                  activityData?.programFrameWork?.map((item: IdNameList) => ({
                    label: item.name,
                    value: item.id,
                  })) as ChipOptionsDataType[]
                }
                chipPopoverGridRef={frameworkRef}
                chipPopoverHeaderLabel={getViewActivityLabel(
                  mdmAddActivitySiteCoreKeys.selectedProgramFramework,
                )}
                isDialogTitleWithEllipsis
                showMoreARIALabel={commonARIALabel.showNMore}
                removeChipNameARIALabel={commonARIALabel.removeChipName}
                closePopoverARIALabel={commonARIALabel.closePopover}
              />
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDoubleLabel
                label={getViewActivityLabel(
                  mdmAddActivitySiteCoreKeys.activityName,
                )}
                value={getValueOrBlank(activityData?.activityName)}
                isValueWithEllipsis
              />
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDoubleLabelWithChip
                label={getViewActivityLabel(
                  mdmAddActivitySiteCoreKeys.modality,
                )}
                replaceEmptyWithDash
                value={
                  activityData?.modality?.map((item: string) => ({
                    label: fetchValueByKeyOfDropdown(
                      mdmAddActivitySiteCoreKeys.activityDetailsKey,
                      mdmAddActivitySiteCoreKeys.modalityList,
                      item,
                    ),
                    value: item,
                  })) as ChipOptionsDataType[]
                }
                chipPopoverGridRef={modalityRef}
                chipPopoverHeaderLabel={getViewActivityLabel(
                  mdmAddActivitySiteCoreKeys.selectedModality,
                )}
                isDialogTitleWithEllipsis
                showMoreARIALabel={commonARIALabel.showNMore}
                removeChipNameARIALabel={commonARIALabel.removeChipName}
                closePopoverARIALabel={commonARIALabel.closePopover}
              />
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDoubleLabel
                label={getViewActivityLabel(mdmAddActivitySiteCoreKeys.units)}
                value={fetchValueByKeyOfDropdown(
                  mdmAddActivitySiteCoreKeys.activityDetailsKey,
                  mdmAddActivitySiteCoreKeys.unitsList,
                  activityData?.unit,
                )}
              />
            </MuiGrid>
          </MuiGrid>
        </AccordionHeader>
        <AccordionHeader
          title={renderHeader(mdmAddActivitySiteCoreKeys.topicListKey)}
          onChange={() => handleIndividualAccordionChange("topics")}
          isExpanded={accordionStatus.topics}
        >
          <MuiBox className="topic-details-grid">
            <ActivityTopicList
              rows={[...topicList]}
              pageId={pageId}
              isViewPage={true}
              handleViewTopic={handleViewTopic}
            />
          </MuiBox>
        </AccordionHeader>
      </MdmViewActivityWrapper>
    </MuiModal>
  );
};
